import { useBunja } from 'bunja/react';
import { ReactNode, useMemo } from 'react';
import { useMount } from 'react-use';

import { getOrderHistoriesApi } from '@flipster/apis/v2/history';
import { orderHistoryInstance } from '@flipster/apis/v2/history/instance';
import { ProductType } from '@flipster/apis/v2/history/types';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { Col } from '@flipster/ui/Table';

import { useAuth } from '../../../shared/auth/state';
import { useStepPagination } from '../../../shared/pagination/use-step-pagination';
import { historyExportState } from '../../state/export';
import ActionButtons from '../action-buttons';
import { DateRangeFilter } from '../date-range-filter';
import { filterSection, left } from '../styles';
import SymbolSelector from '../symbol-selector';
import { TableList } from '../table-list';
import { OrderDesktopRow } from './order-desktop-row';
import { OrderMobileCard } from './order-mobile-card';
import { OrderStatusSelectBox } from './order-status-selectbox';
import { selectors } from './styles';

interface Props {
  fillOrderSelectBox: ReactNode;
  productType: ProductType;
}

export const getOrderHistoryColumns = (productType: ProductType): Col[] => [
  { title: <FormattedMessage id='Common.r250401.Date' />, width: 140, key: 'date' },
  { title: <FormattedMessage id='Assets.r250401.Symbol' />, width: 170, key: 'symbol' },
  {
    title:
      productType === ProductType.Perpetual ? (
        <FormattedMessage id='Assets.r250401.Side' />
      ) : (
        <FormattedMessage id='Autogen_common.r251126.s_Buy_slash_sell' />
      ),
    width: 70,
    key: 'side',
  },
  { title: <FormattedMessage id='Autogen_common.r251126.s_Type' />, width: 100, key: 'type' },
  {
    title: <FormattedMessage id='Autogen_common.r251126.s_Fill_Order_price' />,
    align: 'right',
    width: 140,
    key: 'orderPrice',
  },
  { title: <FormattedMessage id='Autogen_common.r251126.s_Fill_Order_qty' />, align: 'right', width: 140, key: 'qty' },
  { title: <FormattedMessage id='Common.r251204.Fee' />, align: 'right', width: 160, key: 'fee' },
  { title: <FormattedMessage id='Autogen_common.r251126.s_Value' />, align: 'right', width: 90, key: 'value' },
  { title: <FormattedMessage id='Autogen_common.r251126.s_Status' />, align: 'right', width: 120, key: 'status' },
];

export const OrderHistory = ({ fillOrderSelectBox, productType }: Props) => {
  const { isCookieLoggedIn } = useAuth();
  const router = useRouter();
  const { setExportParams } = useBunja(historyExportState);
  const {
    isLoading,
    params,
    data,
    hasPrev,
    hasNext,
    setFilter,
    setRangeFilter,
    setSymbolFilter,
    setPagination,
    resetParams,
    refetch,
  } = useStepPagination('history', ['getOrderHistoriesApi'], getOrderHistoriesApi, {
    passParams: {
      productType,
    },
    enabled: isCookieLoggedIn,
  });

  const onSelectStatus = (value: string | undefined) => {
    setFilter({ hideCanceledOrder: value ? true : undefined });
  };

  useMount(() => {
    resetParams();
  });

  const handleClickExport = () => {
    setExportParams({
      category: productType === ProductType.Perpetual ? 'perpetual' : 'spot',
      subCategory: 'trade',
      siblingCategory: 'order',
      additionalOption: {
        symbol: params?.symbol,
        hideCanceledOrder: params?.hideCanceledOrder,
      },
    });
    router.push('/history/export');
  };

  const range = useMemo(
    () => (params?.startTime && params?.endTime ? { startDate: params.startTime, endDate: params.endTime } : undefined),
    [params?.startTime, params?.endTime],
  );

  return (
    <>
      <section className={filterSection}>
        <div className={left}>
          <DateRangeFilter selected={range} onChange={(range) => setRangeFilter([range?.startDate, range?.endDate])} />
          <div className={selectors}>
            {fillOrderSelectBox}
            <SymbolSelector symbol={params?.symbol} onSelect={setSymbolFilter} productType={productType} />
            <OrderStatusSelectBox value={params.hideCanceledOrder?.toString()} onChange={onSelectStatus} />
          </div>
        </div>
        <ActionButtons refetch={refetch} onClickExport={handleClickExport} />
      </section>

      <TableList
        isLoading={isLoading}
        items={data?.map(orderHistoryInstance)}
        renderCardItem={(item) => (
          <OrderMobileCard item={item} key={`card-${item.orderId}`} productType={productType} />
        )}
        renderTableItem={(item) => (
          <OrderDesktopRow item={item} key={`row-${item.orderId}`} productType={productType} />
        )}
        columns={getOrderHistoryColumns(productType)}
        pagination={{ cursorKey: 'orderId', hasPrev, hasNext, onChange: setPagination }}
      />
    </>
  );
};
